import { useAuth } from 'restaurantAdmin/context/AuthContext';
import { RefundButton } from 'restaurantAdmin/components/refundButton/RefundButton';
import { useMessagingDrawer } from 'restaurantAdmin/messaging/hooks';
import { Button, ButtonVariants } from '@components/button/Button';
import { useIsOpen } from '@shared/hooks/useIsOpen';
import { isOccupantActive } from '@shared/types/occupants';
import { hasEvent, isReservation, type ServiceOccupant } from '../apiHelpers';
import { useAdmitGuest } from '../hooks/useAdmitGuest';
import { useReactivateReservion } from '../hooks/useReactivateReservation';
import { useServiceSearchParams } from '../hooks/useServiceSearchParams';
import { OccupantFinishedConfirmationModal } from '../OccupantFinishedConfirmationModal';
import styles from './OccupantDetailsActions.scss';
import { OccupantDetailsOptionsMenu } from './OccupantDetailsOptionsMenu';
import { CollectCancellationFeeConfirmationModal } from 'restaurantAdmin/components/remediationForm/CollectCancellationFeeConfirmationModal';

interface OccupantDetailsActionsProps {
  occupant: ServiceOccupant;
  fetchOccupant: () => void;
  allowActivationOfSeatMode: boolean;
}

export const OccupantDetailsActions = ({
  occupant,
  fetchOccupant,
  allowActivationOfSeatMode,
}: OccupantDetailsActionsProps) => {
  return (
    <div className={styles.actions}>
      {isOccupantActive(occupant) ? (
        <ActiveOccupantActions
          occupant={occupant}
          fetchOccupant={fetchOccupant}
          allowActivationOfSeatMode={allowActivationOfSeatMode}
        />
      ) : (
        <InactiveOccupantActions
          occupant={occupant}
          fetchOccupant={fetchOccupant}
          allowActivationOfSeatMode={allowActivationOfSeatMode}
        />
      )}
    </div>
  );
};

const ActiveOccupantActions = ({
  occupant,
  fetchOccupant,
  allowActivationOfSeatMode,
}: OccupantDetailsActionsProps) => {
  const { isManager } = useAuth();
  const { closeDrawer, openSeatReservationDrawer, enterReservationEditMode } =
    useServiceSearchParams();
  const {
    isOpen: isModalOpen,
    open: openModal,
    close: closeModal,
  } = useIsOpen();
  const admitGuestMutation = useAdmitGuest(occupant.id, fetchOccupant);
  const isGodModeReservation = isReservation(occupant) && !!occupant.duration;
  const isOccupantAdmitted =
    isReservation(occupant) && occupant.serviceStatus === 'admitted';

  const canOccupantBeAdmitted =
    isReservation(occupant) &&
    hasEvent(occupant) &&
    !occupant.seatedTableName &&
    !occupant.listingFloorPlanId &&
    !isOccupantAdmitted;

  const canFinish = occupant.seatedTableName || isOccupantAdmitted;

  return (
    <>
      {canFinish && (
        <>
          <Button
            label={
              isReservation(occupant) ? 'Guest Finished' : 'Finish Walk-in'
            }
            onClick={openModal}
            variant={ButtonVariants.Primary}
          />
          <OccupantFinishedConfirmationModal
            isOpen={isModalOpen}
            closeModal={() => {
              closeModal();
              closeDrawer();
            }}
            occupant={occupant}
          />
        </>
      )}
      {canOccupantBeAdmitted && (
        <Button
          label="Check-in"
          onClick={() => admitGuestMutation.mutate()}
          variant={ButtonVariants.Primary}
          className={styles.assignTable}
        />
      )}
      {!occupant.seatedTableName && allowActivationOfSeatMode && (
        <Button
          label="Seat Guest"
          onClick={openSeatReservationDrawer}
          variant={
            isOccupantAdmitted || canOccupantBeAdmitted
              ? ButtonVariants.Secondary
              : ButtonVariants.Primary
          }
          className={styles.assignTable}
        />
      )}
      {isReservation(occupant) &&
        (isManager || (!isManager && !isGodModeReservation)) && ( // God mode reservation only visible to manager and non manager when the reservation has no duration
          <Button
            label="Edit Reservation"
            onClick={enterReservationEditMode}
            variant={ButtonVariants.Tertiary}
          />
        )}
      <OccupantDetailsOptionsMenu
        occupant={occupant}
        fetchOccupant={fetchOccupant}
        allowActivationOfSeatMode={allowActivationOfSeatMode}
      />
    </>
  );
};

const InactiveOccupantActions = ({
  occupant,
  fetchOccupant,
  allowActivationOfSeatMode,
}: OccupantDetailsActionsProps) => {
  const { openMessagingDrawer } = useMessagingDrawer();
  const reactivateMutation = useReactivateReservion(occupant.id);
  const { openSeatReservationDrawer, openSeatWalkInFormDrawer } =
    useServiceSearchParams();
  const {
    isOpen: isCollectCancellationFeeModalOpen,
    open: openCollectCancellationFeeModal,
    close: closeCollectCancellationFeeModal,
  } = useIsOpen();

  const handleSeatGuest = () => {
    if (isReservation(occupant)) {
      openSeatReservationDrawer();
    } else {
      openSeatWalkInFormDrawer();
    }
  };

  return (
    <>
      {isReservation(occupant) && (
        <Button
          isDisabled={!occupant.guest.phone}
          label={'Message Guest'}
          onClick={() => openMessagingDrawer([occupant.guest.id])}
          variant={ButtonVariants.Secondary}
        />
      )}
      {allowActivationOfSeatMode && (
        <Button
          label="Seat Guest"
          onClick={handleSeatGuest}
          variant={ButtonVariants.Tertiary}
        />
      )}

      {isReservation(occupant) && (
        <>
          <Button
            label="Reactivate"
            onClick={() => reactivateMutation.mutate()}
            variant={ButtonVariants.Tertiary}
          />
          <RefundButton
            reservationId={occupant.id}
            reservationPrice={occupant.transactionDetails.price}
            isRefunded={!!occupant.transactionDetails.refundAmount}
            buttonVariant={ButtonVariants.Tertiary}
            handleAfterConfirm={fetchOccupant}
            disableModalPortal={false}
          />
        </>
      )}
      {isReservation(occupant) &&
        occupant.isEligibleForCancellationFee &&
        occupant.cancellationPolicy && (
          <>
            <Button
              label="Collect Cancellation Fee"
              onClick={openCollectCancellationFeeModal}
              variant={ButtonVariants.Tertiary}
            />
            <CollectCancellationFeeConfirmationModal
              cancellationPolicy={occupant.cancellationPolicy}
              closeModal={closeCollectCancellationFeeModal}
              handleAfterConfirm={fetchOccupant}
              isOpen={isCollectCancellationFeeModalOpen}
              reservationId={occupant.id}
            />
          </>
        )}
    </>
  );
};
